import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useRef, useState } from 'react';

import { isAllowedPhotoContentType, uploadToS3 } from '@/features/analisis/common/fotos';
import { randomUUID } from '@/utils/uuid';

import type { FotoResponse } from '../types';
import type { FotoTemporal, UseEditarCaptionFn, UseEliminarFotoFn, UseUploadFotoFn } from '../fotosTypes';
import { useUploadFotoTemporal } from '../hooks/useFotosTemporales';
import { CropImageDialog } from './CropImageDialog';
import { FotoCard } from './FotoCard';
import type { FotoLightboxItem } from './FotoLightbox';
import { FotoLightbox } from './FotoLightbox';

interface FotosManagerProps {
  fotos: FotoResponse[];
  fotosTemporales: FotoTemporal[];
  onFotosTemporalesChange: React.Dispatch<React.SetStateAction<FotoTemporal[]>>;
  uidProtocolo: string;
  uidAnalisis: string | null;
  useUploadFoto: UseUploadFotoFn;
  useEditarCaption: UseEditarCaptionFn;
  useEliminarFoto: UseEliminarFotoFn;
  disabled?: boolean;
}

export function FotosManager({
  fotos,
  fotosTemporales,
  onFotosTemporalesChange,
  uidProtocolo,
  uidAnalisis,
  useUploadFoto,
  useEditarCaption,
  useEliminarFoto,
  disabled,
}: FotosManagerProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const [lightboxIndice, setLightboxIndice] = useState<number | null>(null);

  const uploadTemporal = useUploadFotoTemporal();
  const uploadFoto = useUploadFoto();

  // Build the flat lightbox array: persistidas + done temporales only
  const doneTemporales = fotosTemporales.filter((f) => f.uploadStatus === 'done');
  const fotosLightbox: FotoLightboxItem[] = [
    ...fotos.map((f) => ({ url: f.presigned_url, caption: f.caption })),
    ...doneTemporales.map((f) => ({ url: f.blobUrl, caption: f.caption })),
  ];
  // URL → index lookup so FotoCard can open the lightbox at the right position
  const urlToLightboxIndex = new Map(fotosLightbox.map((f, i) => [f.url, i]));

  function openLightbox(url: string): void {
    const idx = urlToLightboxIndex.get(url);
    if (idx !== undefined) setLightboxIndice(idx);
  }

  async function initiateUpload(file: File, localId: string): Promise<void> {
    // Edit mode: persist the photo directly against the analysis (presigned + confirmar).
    // It becomes a persisted foto immediately, so we drop the temporal placeholder.
    if (uidAnalisis) {
      try {
        await uploadFoto.mutateAsync({ uidProtocolo, uidAnalisis, file, caption: null });
        onFotosTemporalesChange((prev) => prev.filter((f) => f.localId !== localId));
      } catch {
        onFotosTemporalesChange((prev) =>
          prev.map((f) => (f.localId === localId ? { ...f, uploadStatus: 'error' } : f)),
        );
      }
      return;
    }

    // Create mode: upload to temporal storage; persisted later via the create payload.
    try {
      const { upload_url, uid_foto } = await uploadTemporal.mutateAsync(file.type);
      await uploadToS3(upload_url, file);
      onFotosTemporalesChange((prev) =>
        prev.map((f) => (f.localId === localId ? { ...f, uid_foto, uploadStatus: 'done' } : f)),
      );
    } catch {
      onFotosTemporalesChange((prev) =>
        prev.map((f) => (f.localId === localId ? { ...f, uploadStatus: 'error' } : f)),
      );
    }
  }

  function handleFileReady(file: File): void {
    const localId = randomUUID();
    const blobUrl = URL.createObjectURL(file);
    onFotosTemporalesChange((prev) => [
      ...prev,
      { localId, uid_foto: '', blobUrl, file, caption: null, uploadStatus: 'uploading' },
    ]);
    void initiateUpload(file, localId);
  }

  function handleRetry(localId: string, file: File): void {
    onFotosTemporalesChange((prev) =>
      prev.map((f) => (f.localId === localId ? { ...f, uploadStatus: 'uploading' } : f)),
    );
    void initiateUpload(file, localId);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isAllowedPhotoContentType(file.type)) {
      setFileTypeError('Solo se permiten imágenes JPEG o PNG.');
      return;
    }
    setFileTypeError(null);
    setCropFile(file);
  }

  const totalFotos = fotos.length + fotosTemporales.length;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Fotos
        {totalFotos > 0 && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({totalFotos})
          </Typography>
        )}
      </Typography>

      {fileTypeError && (
        <Alert severity="error" onClose={() => setFileTypeError(null)} sx={{ mb: 1 }}>
          {fileTypeError}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 1,
        }}
      >
        {fotos.map((foto) =>
          uidAnalisis ? (
            <FotoCard
              key={foto.uid_foto}
              tipo="persistida"
              foto={foto}
              uidProtocolo={uidProtocolo}
              uidAnalisis={uidAnalisis}
              useEditarCaption={useEditarCaption}
              useEliminarFoto={useEliminarFoto}
              disabled={disabled}
              onLightbox={openLightbox}
            />
          ) : null,
        )}

        {fotosTemporales.map((foto) => (
          <FotoCard
            key={foto.localId}
            tipo="temporal"
            foto={foto}
            onFotosTemporalesChange={onFotosTemporalesChange}
            onRetry={handleRetry}
            disabled={disabled}
            onLightbox={openLightbox}
          />
        ))}

        {!disabled && (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              aspectRatio: '4/3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Tooltip title="Agregar foto">
              <IconButton component="span" color="primary" disableRipple>
                <AddPhotoAlternateIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />

      <CropImageDialog
        file={cropFile}
        open={cropFile !== null}
        onApply={(croppedFile) => {
          setCropFile(null);
          handleFileReady(croppedFile);
        }}
        onSkip={() => {
          const file = cropFile;
          setCropFile(null);
          if (file) handleFileReady(file);
        }}
        onCancel={() => setCropFile(null)}
      />

      <FotoLightbox
        open={lightboxIndice !== null}
        onClose={() => setLightboxIndice(null)}
        fotos={fotosLightbox}
        indiceActual={lightboxIndice ?? 0}
        onIndiceChange={setLightboxIndice}
      />
    </Box>
  );
}
